import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getDistributorFundRequests, reviewDistributorFundRequest } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { CheckCircle, XCircle, Clock, Image, Funnel } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function DistributorFundRequestsPage() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [rejectDialog, setRejectDialog] = useState({ open: false, id: null });
  const [rejectReason, setRejectReason] = useState('');

  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'assistant';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const status = filter === 'all' ? undefined : filter;
      const res = await getDistributorFundRequests(status);
      setRequests(res.data);
    } catch (err) {
      toast.error('Failed to load fund requests');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (id) => {
    try {
      await reviewDistributorFundRequest(id, { action: 'approve' });
      toast.success('Fund request approved! Amount credited.');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    try {
      await reviewDistributorFundRequest(rejectDialog.id, { action: 'reject', rejection_reason: rejectReason });
      toast.success('Fund request rejected');
      setRejectDialog({ open: false, id: null });
      setRejectReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };

  const formatCurrency = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n || 0);

  if (!isAdmin) return <div className="p-4 text-red-600">Access denied.</div>;

  const statusIcon = (status) => {
    if (status === 'approved') return <CheckCircle size={16} weight="fill" className="text-green-600" />;
    if (status === 'rejected') return <XCircle size={16} weight="fill" className="text-red-600" />;
    return <Clock size={16} weight="fill" className="text-amber-500" />;
  };

  return (
    <div className="space-y-6" data-testid="dist-fund-requests-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Distributor Fund Requests</h1>
        <div className="flex items-center gap-2">
          <Funnel size={18} className="text-gray-500" />
          <select value={filter} onChange={e => setFilter(e.target.value)} className="border border-gray-200 rounded-md h-9 px-3 text-sm" data-testid="fund-request-filter">
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      <div className="card-flat overflow-x-auto">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : (
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead>TXN ID</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Distributor</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Screenshot</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Reviewed By</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-gray-500">No fund requests</TableCell>
                </TableRow>
              ) : (
                requests.map(req => (
                  <TableRow key={req.id} data-testid={`fund-req-${req.id}`}>
                    <TableCell className="font-mono text-xs text-gray-600">{req.transaction_id || '-'}</TableCell>
                    <TableCell className="text-sm">{formatDate(req.created_at)}</TableCell>
                    <TableCell className="font-medium">
                      {req.agency_names?.length > 0 ? (
                        <>
                          <div className="text-gray-900" data-testid={`fund-req-agency-${req.id}`}>
                            {req.agency_names[0]}
                            {req.agency_names.length > 1 && <span className="text-xs text-gray-500 font-normal"> +{req.agency_names.length - 1}</span>}
                          </div>
                          <div className="text-[11px] text-gray-500 font-normal">Owner: {req.distributor_name}</div>
                        </>
                      ) : (
                        <div className="text-gray-900">{req.distributor_name}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium text-emerald-600">{formatCurrency(req.amount)}</TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate">{req.notes || '-'}</TableCell>
                    <TableCell>
                      {req.screenshot_url ? (
                        <a href={`${window.location.origin}${req.screenshot_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          <Image size={16} /> View
                        </a>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1">
                        {statusIcon(req.status)}
                        <span className={`text-xs font-medium ${req.status === 'approved' ? 'text-green-600' : req.status === 'rejected' ? 'text-red-600' : 'text-amber-600'}`}>
                          {req.status.toUpperCase()}
                        </span>
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{req.reviewed_by || '-'}</TableCell>
                    <TableCell className="text-right">
                      {req.status === 'pending' && (
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" onClick={() => handleApprove(req.id)} className="bg-green-600 hover:bg-green-700 text-white text-xs h-8 px-3" data-testid={`approve-fund-${req.id}`}>
                            <CheckCircle size={14} className="mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setRejectDialog({ open: true, id: req.id })} className="text-red-600 border-red-300 hover:bg-red-50 text-xs h-8 px-3" data-testid={`reject-fund-${req.id}`}>
                            <XCircle size={14} className="mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => { if (!open) setRejectDialog({ open: false, id: null }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Fund Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Rejection reason (optional)" value={rejectReason} onChange={e => setRejectReason(e.target.value)} data-testid="reject-reason-input" />
            <Button onClick={handleReject} className="w-full bg-red-600 hover:bg-red-700" data-testid="confirm-reject-btn">
              Confirm Reject
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
